import { FileUpload, FileUploadDropzone } from '@/components/ui/file-upload'
import { Loader2, Trash2, UploadIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { uploadFile } from 'app/frontend/base/api/team'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { useTranslation } from 'react-i18next'
import React, { PropsWithChildren, useState } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useFormContext } from 'react-hook-form'
import { FieldProps } from 'app/frontend/base/components/form-fields/types'
import Card from 'app/frontend/base/components/Card'

const FileInput = ({
  className,
  customPlaceholder,
  inputClassName,
  onBlur,
  name,
}: PropsWithChildren<FieldProps<string>>) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const [rejection, setRejection] = useState<{
    fileName: string
    message: string
  } | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const form = useFormContext()
  const imageUrl = form.watch(name)

  const handleImageUpload = async (files: File[]) => {
    setRejection(null)
    setIsUploading(true)
    try {
      const response = await uploadFile(team.id, files[0])
      form.setValue(name, response.url)
      onBlur?.({
        target: { value: response.url },
      } as React.FocusEvent<HTMLInputElement>)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <>
      <Tabs
        className={className}
        defaultValue={imageUrl?.includes('wowaudit.com') ? 'upload' : 'url'}
      >
        <div className='flex justify-end'>
          <TabsList>
            <TabsTrigger value='url'>{t('news.image-url')}</TabsTrigger>
            <TabsTrigger value='upload'>{t('news.image-upload')}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value='url'>
          <Input
            placeholder={customPlaceholder ?? 'Image URL'}
            className={inputClassName}
            value={imageUrl || ''}
            onChange={(e) => form.setValue(name, e.target.value)}
            onBlur={onBlur}
          />
        </TabsContent>
        <TabsContent value='upload'>
          <FileUpload
            value={[]}
            onValueChange={handleImageUpload}
            onFileReject={(file, message) =>
              setRejection({ fileName: file.name, message })
            }
            accept='image/*'
            maxFiles={1}
            maxSize={5 * 1024 * 1024}
            className='w-full'
          >
            {imageUrl ? (
              <Card className='h-[120px] flex items-center justify-between gap-2.5 overflow-hidden'>
                <img
                  src={imageUrl}
                  alt='Image'
                  className='h-full w-auto max-w-full flex-1 min-w-0 object-contain'
                />
                <Button
                  variant='outline'
                  size='icon'
                  type='button'
                  className='shrink-0'
                  onClick={() => {
                    form.setValue(name, null)
                    onBlur?.({
                      target: { value: null },
                    } as unknown as React.FocusEvent<HTMLInputElement>)
                  }}
                >
                  <Trash2 className='h-4 w-4 text-destructive-foreground' />
                </Button>
              </Card>
            ) : isUploading ? (
              <div className='h-[120px] flex items-center justify-center rounded-md border border-dashed border-input'>
                <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
              </div>
            ) : (
              <FileUploadDropzone className='h-[120px] cursor-pointer'>
                <div className='flex flex-col items-center gap-2 text-center'>
                  <UploadIcon className='h-8 w-8 text-muted-foreground' />
                  <div className='text-sm text-muted-foreground'>
                    <span className='font-semibold'>Click to upload</span> or
                    drag and drop
                  </div>
                  <div className='text-xs text-muted-foreground'>
                    Images only, up to 5MB
                  </div>
                </div>
              </FileUploadDropzone>
            )}
            {rejection && (
              <Alert variant='destructive' className='mt-3'>
                <AlertTitle>Upload rejected</AlertTitle>
                <AlertDescription>
                  {rejection.fileName}: {rejection.message}
                </AlertDescription>
              </Alert>
            )}
          </FileUpload>
        </TabsContent>
      </Tabs>
    </>
  )
}

export default FileInput
